import { useState } from 'react';
import { Modal, Flex, Text, Button, Skeleton } from '@nula/core-ui';
import { useQuery } from '@tanstack/react-query';
import { previewChangePlan } from './api';
import { formatMoney } from './format';
import type { BillingInterval } from './types';

interface ChangePlanConfirmProps {
  open: boolean;
  planCode: string;
  planName: string;
  interval: BillingInterval;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  /** Scope the proration preview to a specific workspace (defaults to active). */
  workspaceId?: string;
}

function prorationLine(amountDueNowCents: number | null, currency: string | null): string {
  if (amountDueNowCents === null || currency === null) return 'Your bill will be prorated for the change.';
  if (amountDueNowCents > 0) {
    return `You'll be charged ${formatMoney(amountDueNowCents, currency)} today (prorated).`;
  }
  if (amountDueNowCents < 0) {
    return `You'll receive a ${formatMoney(-amountDueNowCents, currency)} credit toward your next invoice.`;
  }
  return 'No charge today. The change is covered by your current balance.';
}

export function ChangePlanConfirm({
  open,
  planCode,
  planName,
  interval,
  onClose,
  onConfirm,
  workspaceId,
}: ChangePlanConfirmProps) {
  const [confirming, setConfirming] = useState(false);
  const preview = useQuery({
    queryKey: ['billing', 'change-plan-preview', planCode, interval, workspaceId ?? null] as const,
    queryFn: () => previewChangePlan(planCode, interval, workspaceId),
    enabled: open,
  });

  const handleConfirm = async () => {
    setConfirming(true);
    try {
      await onConfirm();
    } finally {
      setConfirming(false);
    }
  };

  const perMonth = (cents: number | null, currency: string | null) => {
    if (cents === null || currency === null) return null;
    const monthly = interval === 'year' ? Math.round(cents / 12) : cents;
    return `${formatMoney(monthly, currency)}/mo`;
  };

  const footer = (
    <>
      <Button variant="text" onClick={onClose} disabled={confirming}>
        Cancel
      </Button>
      <Button variant="primary" pressEffect="glow" loading={confirming} onClick={() => void handleConfirm()}>
        Confirm switch
      </Button>
    </>
  );

  return (
    <Modal open={open} onClose={onClose} title={`Switch to ${planName}?`} footer={footer} centered>
      {preview.isLoading ? (
        <Skeleton active data-testid="change-plan-preview-skeleton" />
      ) : (
        <Flex vertical gap="sm">
          <Text>{prorationLine(preview.data?.amountDueNowCents ?? null, preview.data?.currency ?? null)}</Text>
          {perMonth(preview.data?.recurringAmountCents ?? null, preview.data?.currency ?? null) && (
            <Text size="sm" type="secondary">
              Then {perMonth(preview.data?.recurringAmountCents ?? null, preview.data?.currency ?? null)} going
              forward.
            </Text>
          )}
        </Flex>
      )}
    </Modal>
  );
}
